import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO_IMG, CONTACT } from "@/data";

const LINE1 = ["Your", "garage,"];
const LINE2 = ["reimagined."];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } } };
const word = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen bg-paper overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="A clean, organized garage" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-paper via-paper/85 to-paper/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-paper/85 via-transparent to-paper/30" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-20 min-h-screen flex flex-col justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-rust">Locally Owned</span>
          <span className="w-12 h-px bg-ink/30" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-ink/60">One-Day Service</span>
        </motion.div>

        <motion.h1 variants={container} initial="hidden" animate="show"
          className="font-display font-black tracking-tight text-6xl sm:text-7xl lg:text-[8rem] leading-[0.9] text-ink max-w-4xl">
          <span className="block overflow-hidden">
            <span className="flex gap-4">
              {LINE1.map((w, i) => (
                <motion.span key={i} variants={word} className="inline-block">{w}</motion.span>
              ))}
            </span>
          </span>
          <span className="block overflow-hidden">
            {LINE2.map((w, i) => (
              <motion.span key={i} variants={word} className="inline-block text-rust">{w}</motion.span>
            ))}
          </span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-xl text-lg text-ink/70 leading-relaxed">
          Full cleanouts, spotless floors, smart storage and same-day upgrades — handled start to finish by one crew,
          so you get your space back without lifting a finger.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/estimate" data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-2 bg-ink text-paper px-8 py-4 font-semibold hover:bg-rust transition-colors">
            Get a Free Estimate
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <a href="#dream" data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2 border border-ink/25 text-ink px-8 py-4 font-semibold bg-paper/50 backdrop-blur-sm hover:bg-ink hover:text-paper transition-colors">
            Try the AI Preview
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-4">
          <a href={CONTACT.phoneHref} data-testid="hero-phone"
            className="inline-flex items-center gap-2 text-rust font-display font-bold text-xl hover:opacity-80 transition-opacity">
            <Phone size={20} className="fill-rust" /> {CONTACT.phone}
          </a>
          <div className="flex items-center gap-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-rust text-rust" />)}
            </div>
            <span className="font-mono text-xs text-ink/70">5.0 · Loved by local homeowners</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
